import React, { useEffect, useRef, useState } from "react";

import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { ChevronUpIcon } from "@/components/Icons/ChevronUpIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { recipeDisplayTitle } from "./RecipesSection.constants";
import { RecipeLogo } from "./RecipeLogo";
import { SettingsSectionCard } from "./SettingsSectionCard";
import { useRecipesSection } from "../hooks/useRecipesSection";
import { RecipesSectionDialog } from "./RecipesSectionDialog";
import { HardwareSelectionWarning } from "./HardwareSelectionWarning";

type RecipesSectionProps = {
  /** True when user chose Custom Deployment from recipes-first; stacks start collapsed if no recipe. */
  skippedRecipesFirst?: boolean;
};

export const RecipesSection: React.FC<RecipesSectionProps> = ({ skippedRecipesFirst = false }) => {
  const {
    values,
    isDialogOpen,
    showHardwareSelectionWarning,
    setIsDialogOpen,
    visibleRecipes,
    allProcessedRecipes,
    onSelect,
    onDialogRecipeRowClick,
    onCloseWarning,
    onConfirmWarning
  } = useRecipesSection();

  const hasRecipe = Boolean(values.containerRecipe);
  const [expandedWithoutRecipe, setExpandedWithoutRecipe] = useState(false);
  const hadRecipeRef = useRef(hasRecipe);

  useEffect(() => {
    if (hadRecipeRef.current && !hasRecipe) {
      setExpandedWithoutRecipe(false);
    }
    hadRecipeRef.current = hasRecipe;
  }, [hasRecipe]);

  const showStacksPicker = !skippedRecipesFirst || hasRecipe || expandedWithoutRecipe;

  return (
    <SettingsSectionCard
      title="Pre-Configured Recipes"
      description="Select a recipe and a GPU to skip the setup. We've pre-configured the environment, ports, and vLLM parameters for you."
      savedInConfig
    >
      {!showStacksPicker ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-gray-dark-600 px-4 py-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => setExpandedWithoutRecipe(true)}
          >
            View Recipes
            <ChevronDownIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="_overflow-hidden rounded-lg border border-border">
          {!hasRecipe ? (
            <div className="flex justify-end border-b border-gray-dark-200 rounded-t-lg bg-gray-dark-600 px-4 py-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setExpandedWithoutRecipe(false)}
              >
                Hide Recipes
                <ChevronUpIcon className="ml-2 h-4 w-4" />
              </Button>
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-4 bg-muted/30 p-4 md:grid-cols-2">
            {visibleRecipes.map((recipe) => (
              <button
                key={recipe.key}
                type="button"
                className={cn(
                  "rounded-xl flex w-full cursor-pointer flex-col justify-between border bg-background p-5 text-left shadow-sm outline-none transition-[border-color,box-shadow,background-color] hover:border-primary/35 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  values.containerRecipe === recipe.key
                    ? "border-primary bg-accent shadow-md ring-1 ring-primary/15"
                    : "border-gray-dark-200",
                  (recipe.isSoldOut || !recipe.isCompatible) &&
                  "pointer-events-none cursor-not-allowed bg-muted/40 opacity-60 hover:border-gray-dark-200 hover:shadow-sm"
                )}
                onClick={() => !(recipe.isSoldOut || !recipe.isCompatible) && onSelect(recipe.key)}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <RecipeLogo logoUrl={recipe.logoUrl} alt={recipeDisplayTitle(recipe)} />
                    <div className="subtext-medium line-clamp-2 min-w-0 flex-1 font-semibold leading-6 tracking-tight">
                      {recipeDisplayTitle(recipe)}
                    </div>
                  </div>
                  {recipe.tags && recipe.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {recipe.tags.map((tag, idx) => (
                        <Badge
                          key={`${recipe.key}-${idx}`}
                          variant="subtle"
                          className="rounded-lg font-normal"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                  {!recipe.isCompatible ? (
                    <div className="text-xs font-semibold text-muted-foreground">
                      {recipe.availabilityLabel}
                    </div>
                  ) : recipe.isSoldOut ? (
                    <div className="text-xs font-semibold text-muted-foreground">
                      Hardware Sold Out
                    </div>
                  ) : null}
                  <div className="subtext-small line-clamp-3 leading-[20px] text-muted-foreground">
                    {recipe.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
          <HardwareSelectionWarning
            showHardwareSelectionWarning={showHardwareSelectionWarning}
            onCloseWarning={onCloseWarning}
            onConfirm={onConfirmWarning}
          />
          <div className="bg-muted/30 px-4 pb-4">
            <RecipesSectionDialog
              isDialogOpen={isDialogOpen}
              setIsDialogOpen={setIsDialogOpen}
              currentRecipeKey={values.containerRecipe}
              onDialogRecipeRowClick={onDialogRecipeRowClick}
              recipes={allProcessedRecipes}
            />
          </div>
        </div>
      )}
    </SettingsSectionCard>
  );
};
